import { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, CheckCircle } from 'lucide-react'
import { useAuth } from '@wrenchlab-ui/hooks/useAuth'
import { Input } from '@wrenchlab-ui/components/ui/Input'
import { Button } from '@wrenchlab-ui/components/ui/Button'

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await forgotPassword(email.trim())
      setSent(true)
    } catch {
      setError('Could not send reset email. Please try again or contact support.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="WrenchLab" className="h-24 w-auto object-contain" />
        </div>

        <div className="wl-card p-6">
          <h1 className="text-lg font-semibold text-neutral-900 mb-1">Reset your password</h1>
          <p className="text-sm text-neutral-500 mb-5">
            Enter your email and we'll send you a reset link.
          </p>

          {sent ? (
            <div className="flex gap-3 rounded-wl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p>
                If an account exists for <strong>{email}</strong>, a reset link has been sent.
                Check your inbox.
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 flex gap-3 rounded-wl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>{error}</p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                />
                <Button type="submit" variant="primary" className="w-full" loading={loading}>
                  Send Reset Link
                </Button>
              </form>
            </>
          )}

          <p className="text-center text-xs text-neutral-400 mt-5">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-primary-600 hover:underline"
            >
              Back to sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
